import PersonIcon from "@mui/icons-material/Person";
import { Box } from "@mui/material";
import React from "react";
import ReactTimeAgo from "react-time-ago";

import { ImageCanvas } from "components/common/ImageCanvas";
import { getCanvasAnnotationsFromROIs, getIQImage } from "features/canvas/utils";
import { Typography } from "new-components/typography/Typography";
import { LabelValue } from "types/labelValue";
import { colorClassNameMap, stringClassNameMap } from "utils/disagreementsUtils";

export interface ImageQueryExampleProps {
  answerOwner: "labeler" | "other";
  imageQuery: LabelValue;
  isLargeMode: boolean;
}

export function ImageQueryExample(props: ImageQueryExampleProps) {
  const { imageQuery, isLargeMode, answerOwner } = props;

  const titleText = answerOwner === "labeler" ? "labeler answer:" : "Other’s answer:";
  const stringAnswer = stringClassNameMap[imageQuery.class_name] || "Unclear";
  const colorAnswer = colorClassNameMap[imageQuery.class_name] || "#535353";
  const annotations = getCanvasAnnotationsFromROIs(imageQuery.rois ?? []);

  const [imageStatus, setImageStatus] = React.useState<"loading" | "loaded" | "failed">("loading");

  const image = React.useMemo(() => {
    const img = getIQImage(imageQuery.iq_id);

    img.onload = () => {
      setImageStatus("loaded");
    };

    img.onerror = () => {
      setImageStatus("failed");
    };

    return img;
  }, [imageQuery.iq_id]);

  return (
    <Box className="relative w-full h-full">
      {isLargeMode && (
        <Box className={`flex justify-start gap-2 ${answerOwner === "labeler" ? "mb-4" : ""}`}>
          <Typography variant="main" fontWeight="bold" textTransform="uppercase" className="text-gray-500">
            {titleText}
          </Typography>
          <Typography variant="main" fontWeight="bold" textTransform="uppercase" sx={{ color: colorAnswer }}>
            {stringAnswer}
          </Typography>
        </Box>
      )}

      {isLargeMode && answerOwner === "other" && (
        <Typography variant="small" textTransform="uppercase" className="text-gray-500">
          (Labeler-customer)
        </Typography>
      )}

      <Box
        sx={{
          height: "calc(100% - 48px)",
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <ImageCanvas
          imageData={[image, imageStatus]}
          annotations={annotations}
          style={{ cursor: "pointer" }}
          iqId={imageQuery.iq_id}
        />
      </Box>

      <Box
        className="flex justify-between items-center w-full h-7 px-3 bottom-0 absolute"
        sx={{
          backgroundColor: colorAnswer,
        }}
      >
        <Box className="flex gap-2 items-center">
          <Typography variant="detail" className="text-white">
            <PersonIcon />
          </Typography>
          <Typography variant="detail" fontWeight="semibold" noWrap>
            {stringAnswer.toLowerCase()}
          </Typography>
        </Box>
        <Typography variant="detail" fontWeight="semibold" className="text-white">
          <ReactTimeAgo date={new Date(imageQuery.created_at || "")} locale="en-US" />
        </Typography>
      </Box>
    </Box>
  );
}
